import * as stylex from '@stylexjs/stylex';
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { object as vObject, string as vString, optional as vOptional } from 'valibot';

import {
  ArrowRightStrokeIcon,
  Button,
  BUTTON_SLOTS,
  Divider,
  Heading,
  LabeledInput,
  minDelay,
  Text,
  TEXT_BUTTON_SLOTS,
  TextButton,
  TextField,
  TOAST_TIMEOUT
} from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';
import { RouterTextButtonLink } from '@/libs/tanstack-router/components/router-text-button-link.component';
import { authQueries } from '@/resources/auth/auth.api';
import { profileQueries } from '@/resources/profile/profile.api';
import { tryCatch } from '@/utils/try-catch.util';

export const Route = createFileRoute('/_auth/api/v1/auth/google/callback')({
  validateSearch: vObject({
    error: vOptional(vString()),
    state: vString(),
    code: vOptional(vString()),
  }),
  pendingMinMs: 0,
  pendingMs: 0,
  loaderDeps: ({ search }) => ({ code: search.code, state: search.state, error: search.error }),
  pendingComponent: PendingState,
  loader: async ({ context, deps }) => {
    if (deps.error || !deps.code) {
      throw redirect({ to: '/sign-in' });
    }

    if (localStorage.getItem('access_token')) {
      throw redirect({ to: '/create-profile' });
    }

    const { data, error } = await tryCatch(context.queryClient.ensureQueryData(authQueries.googleCallback({ code: deps.code, state: deps.state })));
    if (error || !data) {
      context.toastQueue.add({ message: 'Something went wrong.', variant: 'error' }, { timeout: TOAST_TIMEOUT });
      throw redirect({ to: '/sign-in' });
    }

    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);

    const { error: profileError } = await minDelay(tryCatch(context.queryClient.ensureQueryData(profileQueries.get)), 750);

    if (profileError) {
      sessionStorage.setItem('google_user_info', JSON.stringify(data.user_info));
      sessionStorage.removeItem('google_login_url');
      throw redirect({ to: '/create-profile' });
    }

    throw redirect({ to: '/' });
  },
});

function PendingState() {
  const router = useRouter();

  const isSignIn = router.routesByPath['/sign-in'].fullPath === window.sessionStorage.getItem('google_login_url');

  return (
    <>
      <div {...stylex.props(styles.base)}>
        <div {...stylex.props(formStyles.container)}>
          <form
            aria-disabled
            {...stylex.props(formStyles.base)}>
            <Heading level={1} size={28}>{isSignIn ? 'Sign in' : 'Create Reader account'}</Heading>
            <TextField
              autoComplete="off"
              id="email"
              isDisabled
              name="email"
              type="email">
              <LabeledInput label="Email" />
            </TextField>
          </form>

          <Button
            isDisabled
            isRounded
            size={48}>
            <Text>Continue</Text>
            <ArrowRightStrokeIcon slot={BUTTON_SLOTS.RIGHT} />
          </Button>
        </div>

        <div {...stylex.props(styles.dividerContainer)}>
          <Divider size={0.5} variant="silent" />
          <Text color="tertiary" size={13}>or</Text>
          <Divider size={0.5} variant="silent" />
        </div>

        <Button
          isPending
          isRounded
          size={52}
          variant="alternative">
          Continue with Google
        </Button>

        {isSignIn ?
          <Text color="tertiary" size={16} style={typographyStyles.textAction}>
            Not a member yet?
            <TextButton isDisabled size={48}>
              Create account
              <ArrowRightStrokeIcon size={16} slot={TEXT_BUTTON_SLOTS.RIGHT_ICON} />
            </TextButton>
          </Text> :
          <Text color="tertiary" size={16} style={typographyStyles.textAction}>
            Already a member?
            <TextButton isDisabled size={48}>
              Sign in
              <ArrowRightStrokeIcon size={16} slot={TEXT_BUTTON_SLOTS.RIGHT_ICON} />
            </TextButton>
          </Text>}
      </div>
      <footer {...stylex.props(footerStyles.base)}>
        <RouterTextButtonLink safeArea="square" size={28} target="_blank" to="/privacy-and-terms/$" variant="tertiary">Privacy and terms</RouterTextButtonLink>
      </footer>
    </>
  );
}

const styles = stylex.create({
  base: {
    display: 'flex',
    flex: '1 0 0',
    flexDirection: 'column',
    gap: 32,
    maxWidth: 432,
    paddingLeft: 20,
    paddingRight: 20,
    paddingTop: {
      default: 120,
      [breakpoints.large]: 140,
    },
    width: '100%',
  },
  dividerContainer: {
    alignItems: 'center',
    alignSelf: 'stretch',
    display: 'flex',
    gap: 12,
    justifyContent: 'center',
  },
});

const typographyStyles = stylex.create({
  textAction: {
    display: 'inline-flex',
    gap: 8,
  }
});

const formStyles = stylex.create({
  base: {
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  container: {
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  }
});

const footerStyles = stylex.create({
  base: {
    alignContent: 'flex-start',
    alignItems: 'flex-start',
    bottom: 0,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'center',
    paddingBlock: 2,
    width: '100%',
  },
});
